import { useEffect, useState, type ReactNode } from "react";
import type { TerminalAppModel } from "../state/ui-types";
import { WalletHeader } from "./WalletHeader";
import { getPresentationPolicy } from "../state/presentation-policy";
import { useReducedMotion } from "./use-reduced-motion";

interface AppLayoutProps {
  projectToken?: { address: string; symbol: string } | null;
  model: TerminalAppModel;
  navigation: ReactNode;
  children: ReactNode;
  launchpadFees?: boolean;
  walletPolicy?: "authenticate" | "connect-only";
  onWalletAction(action: () => void | Promise<void>, fallback: string): void;
  onSelectSourceToken(address: string): void;
}

/** Shared page frame: keep main routes on the same header and responsive layout. */
export function AppLayout({ model, projectToken, navigation, children, walletPolicy = "authenticate", launchpadFees = false, onWalletAction, onSelectSourceToken }: AppLayoutProps) {
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    update();
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  const policy = getPresentationPolicy({ effects: model.effects, reducedMotion, visible, mining: model.mining.phase === "MINING" });
  return <div className={`app-root effects-${policy.effects.toLowerCase()}`} data-motion={policy.animate ? "animated" : "still"} data-environment={model.environment}>
    <div className="forest-noise" aria-hidden="true" />
    {/* {model.environment !== "PRODUCTION" ? <div className="environment-banner" role="note">
      <span>{model.environmentLabel}</span>
      <p>{model.environmentNote}</p>
      <strong>MAINNET WRITES {model.environment === "READ_ONLY" ? "OFF" : "NOT ACTIVE"}</strong>
    </div> : null} */}
    <div className="app-frame">
      <WalletHeader
        model={model}
        projectToken={projectToken ?? null}
        walletPolicy={walletPolicy}
        launchpadFees={launchpadFees}
        navigation={navigation}
        onConnect={() => onWalletAction(walletPolicy === "connect-only" ? model.actions.connectWalletOnly : model.actions.connectWallet, "Wallet connection could not be completed.")}
        onChooseWallet={(id) => onWalletAction(() => walletPolicy === "connect-only" ? model.actions.connectWalletOnly(id) : model.actions.connectWallet(id), "Wallet connection could not be completed.")}
        onDisconnect={() => onWalletAction(model.actions.disconnectWallet, "Wallet disconnection could not be completed.")}
        onSwitchNetwork={() => onWalletAction(model.actions.switchNetwork, "Wallet request could not be completed.")}
        onSelectSourceToken={onSelectSourceToken}
      />
      {children}
    </div>
  </div>;
}
